import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Modal from "@/components/common/Modal";
import {
  Wallet,
  Copy,
  Check,
  ArrowDownToLine,
  ArrowUpFromLine,
  RefreshCw,
  LockKeyhole,
  Network,
  Plus,
  ShieldAlert,
} from "lucide-react";

type CryptoWallet = {
  id: string;
  user_id: string;
  asset_id: string;
  network_id: string;
  network_name: string;
  network_code: string;
  chain_type: string;
  confirmations_required: number;
  blockchain_address?: string | null;
  available_balance: string | number;
  locked_balance: string | number;
  created_at?: string;
  updated_at?: string;
};

function formatWalletAmount(value: string | number): string {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue === 0) {
    return "0";
  }

  return numericValue.toLocaleString(undefined, {
    maximumFractionDigits: 8,
  });
}

type CryptoAsset = {
  id: string;
  symbol: string;
  name: string;
  decimals: number;
  is_active: boolean;
};

type CryptoNetwork = {
  id: string;
  asset_id: string;
  network_code: string;
  network_name: string;
  chain_type: string;
  confirmations_required: number;
  is_active: boolean;
};

type CryptoTransaction = {
  id: string;
  transaction_type?: string;
  direction?: string;
  amount?: string | number;
  fee_amount?: string | number;
  status?: string;
  reference?: string;
  blockchain_tx_hash?: string | null;
  description?: string | null;
  created_at?: string;
};

class ApiRequestError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
  }
}

async function api<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options?.headers || {}),
    },
    ...options,
  });

  if (!response.ok) {
    const responseText = await response.text();
    let message = responseText;

    try {
      const parsed = JSON.parse(responseText);
      message =
        parsed?.detail ||
        parsed?.message ||
        responseText;
    } catch {
      // Keep the original response text.
    }

    throw new ApiRequestError(
      message || `Request failed: ${response.status}`,
      response.status,
    );
  }

  return response.json();
}
export default function WalletPage() {
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [selectedNetworkId, setSelectedNetworkId] = useState("");
  const [copied, setCopied] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [showCreateWallet, setShowCreateWallet] = useState(false);
  const [showDeposit, setShowDeposit] = useState<string | null>(null);

  const [withdrawWallet, setWithdrawWallet] =
    useState<CryptoWallet | null>(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawAddress, setWithdrawAddress] = useState("");
  const [withdrawTag, setWithdrawTag] = useState("");
  const [withdrawPin, setWithdrawPin] = useState("");
  const [withdrawError, setWithdrawError] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);

  const assetsQuery = useQuery({
    queryKey: ["crypto-assets"],
    queryFn: () =>
      api<CryptoAsset[]>("/api/v1/crypto/assets?active_only=true"),
  });

  const networksQuery = useQuery({
    queryKey: ["crypto-networks", selectedAssetId],
    enabled: Boolean(selectedAssetId),
    queryFn: () =>
      api<CryptoNetwork[]>(
        `/api/v1/crypto/assets/${selectedAssetId}/networks?active_only=true`,
      ),
  });

  const walletsQuery = useQuery({
    queryKey: ["crypto-wallets"],
    queryFn: () =>
      api<{ wallets: CryptoWallet[] }>("/api/v1/crypto/wallets"),
  });

  const transactionsQuery = useQuery({
    queryKey: ["crypto-transactions"],
    queryFn: () =>
      api<{
        transactions: CryptoTransaction[];
        limit: number;
        offset: number;
      }>("/api/v1/crypto/transactions"),
  });

  const transactionPinQuery = useQuery({
    queryKey: ["transaction-pin-status"],
    enabled: Boolean(withdrawWallet),
    queryFn: () =>
      api<{
        transaction_pin_configured: boolean;
        pin_locked: boolean;
        pin_locked_until: string | null;
      }>("/api/v1/users/security/transaction-pin"),
  });

  const assets = assetsQuery.data || [];
  const networks = networksQuery.data || [];
  const wallets = walletsQuery.data?.wallets || [];
  const transactions = transactionsQuery.data?.transactions || [];

  const assetMap = useMemo(
    () => new Map(assets.map((asset) => [asset.id, asset])),
    [assets],
  );


  const existingNetworkIds = useMemo(
    () => new Set(wallets.map((wallet) => wallet.network_id)),
    [wallets],
  );

  const copyAddress = async (address: string) => {
    await navigator.clipboard.writeText(address);
    setCopied(address);
    window.setTimeout(() => setCopied(""), 2000);
  };

  const createWallet = async () => {
    if (!selectedAssetId || !selectedNetworkId) {
      setCreateError("Please select an asset and network.");
      return;
    }

    const selectedNetwork = networks.find(
      (network) => network.id === selectedNetworkId,
    );

    if (!selectedNetwork) {
      setCreateError("Please select a valid network.");
      return;
    }

    if (selectedNetwork.chain_type !== "EVM") {
      setCreateError(
        `${selectedNetwork.network_name} is not yet supported for wallet creation.`,
      );
      return;
    }

    if (existingNetworkIds.has(selectedNetworkId)) {
      setCreateError("You already have a wallet on this network.");
      return;
    }

    setCreating(true);
    setCreateError("");

    try {
      await api<CryptoWallet>("/api/v1/crypto/wallets", {
        method: "POST",
        body: JSON.stringify({
          asset_id: selectedAssetId,
          network_id: selectedNetworkId,
        }),
      });

      await walletsQuery.refetch();

      setSelectedNetworkId("");
      setShowCreateWallet(false);
    } catch (error) {
      setCreateError(
        error instanceof Error
          ? error.message
          : "Unable to create wallet.",
      );
    } finally {
      setCreating(false);
    }
  };

  const openWithdrawal = (wallet: CryptoWallet) => {
    setWithdrawWallet(wallet);
    setWithdrawAmount("");
    setWithdrawAddress("");
    setWithdrawTag("");
    setWithdrawPin("");
    setWithdrawError("");
  };

  const closeWithdrawal = () => {
    if (withdrawing) {
      return;
    }

    setWithdrawWallet(null);
    setWithdrawAmount("");
    setWithdrawAddress("");
    setWithdrawTag("");
    setWithdrawPin("");
    setWithdrawError("");
  };

  const submitWithdrawal = async () => {
    if (!withdrawWallet) {
      return;
    }

    const amount = Number(withdrawAmount);

    if (!Number.isFinite(amount) || amount < 5) {
      setWithdrawError("Minimum withdrawal amount is 5 USDT.");
      return;
    }

    if (!withdrawAddress.trim()) {
      setWithdrawError("Enter the destination wallet address.");
      return;
    }

    if (!/^\d{4}(?:\d{2})?$/.test(withdrawPin)) {
      setWithdrawError("Transaction PIN must contain 4 or 6 digits.");
      return;
    }

    if (!transactionPinQuery.data?.transaction_pin_configured) {
      setWithdrawError(
        "Transaction PIN is not configured. Configure it in Settings → Security before withdrawing.",
      );
      return;
    }

    if (transactionPinQuery.data.pin_locked) {
      setWithdrawError(
        "Your Transaction PIN is temporarily locked. Please wait until the lock expires before trying again.",
      );
      return;
    }

    setWithdrawing(true);
    setWithdrawError("");

    try {
      await api("/api/v1/crypto/withdrawals", {
        method: "POST",
        body: JSON.stringify({
          wallet_id: withdrawWallet.id,
          transaction_pin: withdrawPin,
          amount: withdrawAmount.trim(),
          destination_address: withdrawAddress.trim(),
          ...(withdrawTag.trim()
            ? { destination_tag: withdrawTag.trim() }
            : {}),
        }),
      });

      await Promise.all([
        walletsQuery.refetch(),
        transactionsQuery.refetch(),
      ]);

      closeWithdrawal();
    } catch (error) {
      if (error instanceof ApiRequestError) {
        if (error.status === 409) {
          setWithdrawError(
            "Transaction PIN is not configured. Configure it in Settings → Security before withdrawing.",
          );
        } else if (error.status === 423) {
          setWithdrawError(
            "Your Transaction PIN is temporarily locked. Please wait until the lock expires before trying again.",
          );
          void transactionPinQuery.refetch();
        } else {
          setWithdrawError(
            error.message || "Unable to submit withdrawal.",
          );
        }
      } else {
        setWithdrawError(
          error instanceof Error
            ? error.message
            : "Unable to submit withdrawal.",
        );
      }
    } finally {
      setWithdrawing(false);
    }
  };

  const refresh = () => {
    void walletsQuery.refetch();
    void transactionsQuery.refetch();

    if (selectedAssetId) {
      void networksQuery.refetch();
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-3">
              <Wallet className="h-6 w-6 text-primary" />
            </div>

            <div>
              <h1 className="text-2xl font-bold">Crypto Wallet</h1>
              <p className="text-sm text-muted-foreground">
                Manage your crypto balances, deposits and withdrawals.
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={refresh}
          className="inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {wallets.length === 0 && (
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <div className="mb-5">
            <h2 className="text-lg font-semibold">Create your wallet</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Select an asset and a supported network to create your MarisX
              wallet.
            </p>
          </div>

          <WalletCreationForm
            assets={assets}
            networks={networks}
            selectedAssetId={selectedAssetId}
            selectedNetworkId={selectedNetworkId}
            onAssetChange={(assetId) => {
              setSelectedAssetId(assetId);
              setSelectedNetworkId("");
              setCreateError("");
            }}
            onNetworkChange={(networkId) => {
              setSelectedNetworkId(networkId);
              setCreateError("");
            }}
            onCreate={createWallet}
            creating={creating}
            loadingNetworks={networksQuery.isLoading}
            existingNetworkIds={existingNetworkIds}
            error={createError}
          />
        </div>
      )}

      {walletsQuery.isLoading && (
        <div className="rounded-xl border bg-card p-8 text-center text-muted-foreground">
          Loading wallets...
        </div>
      )}

      {walletsQuery.isError && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          Unable to load your crypto wallets. Please refresh and try again.
        </div>
      )}

      {wallets.length > 0 && (
        <>
          <div className="grid gap-5 lg:grid-cols-2">
            {wallets.map((wallet) => {
              const asset = assetMap.get(wallet.asset_id);

              const symbol = asset?.symbol || "Crypto";
              const networkName =
                wallet.network_name;
              const networkCode =
                wallet.network_code;
              const address = wallet.blockchain_address || "";

              return (
                <div
                  key={wallet.id}
                  className="rounded-xl border bg-card p-5 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-primary/10 p-3">
                        <Wallet className="h-5 w-5 text-primary" />
                      </div>

                      <div>
                        <h2 className="font-semibold">
                          {symbol} Wallet
                        </h2>

                        <p className="text-sm font-medium text-foreground">
                          {networkName} ({networkCode})
                        </p>
                      </div>
                    </div>

                    <span className="rounded-full bg-green-500/10 px-3 py-1 text-xs font-medium text-green-600">
                      Active
                    </span>
                  </div>

                  <div className="mt-5 rounded-lg bg-muted/50 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Available Balance
                    </p>

                    <p className="mt-1 text-2xl font-bold">
                      {formatWalletAmount(wallet.available_balance)} {symbol}
                    </p>

                    {Number(wallet.locked_balance) > 0 && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        Locked: {formatWalletAmount(wallet.locked_balance)} {symbol}
                      </p>
                    )}
                  </div>

                  <div className="mt-4">
                    <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Deposit Address
                    </p>

                    {address ? (
                      <div className="flex items-center gap-2 rounded-lg border p-3">
                        <code className="min-w-0 flex-1 break-all text-xs md:text-sm">
                          {address}
                        </code>

                        <button
                          type="button"
                          onClick={() => void copyAddress(address)}
                          className="shrink-0 rounded-md p-2 hover:bg-muted"
                          title="Copy address"
                        >
                          {copied === address ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                        Wallet address is being prepared. Refresh shortly.
                      </div>
                    )}
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setShowDeposit(
                          showDeposit === wallet.id ? null : wallet.id,
                        )
                      }
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground"
                    >
                      <ArrowDownToLine className="h-4 w-4" />
                      Deposit
                    </button>

                    <button
                      type="button"
                      onClick={() => openWithdrawal(wallet)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-muted"
                    >
                      <ArrowUpFromLine className="h-4 w-4" />
                      Withdraw
                    </button>
                  </div>

                  {showDeposit === wallet.id && (
                    <div className="mt-4 rounded-lg border bg-muted/30 p-4">
                      <div className="flex items-center gap-2 font-medium">
                        <Network className="h-4 w-4" />
                        Deposit Instructions
                      </div>

                      <div className="mt-3 rounded-lg border bg-background p-3">
                        <p className="text-sm font-semibold">
                          {symbol} on {networkName} ({networkCode})
                        </p>

                        <p className="mt-1 text-xs text-muted-foreground">
                          This wallet address is specifically for this
                          network.
                        </p>
                      </div>

                      <div className="mt-3 flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />

                        <p className="text-sm">
                          Send only <strong>{symbol}</strong> using{" "}
                          <strong>
                            {networkName} ({networkCode})
                          </strong>{" "}
                          to this address. Do not send the asset through
                          another network.
                        </p>
                      </div>

                      <div className="mt-3 rounded-md bg-background p-3 text-xs">
                        <div>
                          <span className="font-medium">Network:</span>{" "}
                          {networkName} ({networkCode})
                        </div>

                        <div className="mt-1">
                          <span className="font-medium">
                            Confirmations:
                          </span>{" "}
                          {wallet.confirmations_required ?? "Required"}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <button
              type="button"
              onClick={() => {
                setShowCreateWallet(!showCreateWallet);
                setCreateError("");
              }}
              className="flex w-full items-center justify-between gap-4 text-left"
            >
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-primary/10 p-3">
                  <Plus className="h-5 w-5 text-primary" />
                </div>

                <div>
                  <h2 className="font-semibold">Create Another Wallet</h2>
                  <p className="text-sm text-muted-foreground">
                    Add a wallet on another supported network.
                  </p>
                </div>
              </div>

              <span className="text-sm font-medium text-primary">
                {showCreateWallet ? "Close" : "Add Wallet"}
              </span>
            </button>

            {showCreateWallet && (
              <div className="mt-5 border-t pt-5">
                <WalletCreationForm
                  assets={assets}
                  networks={networks}
                  selectedAssetId={selectedAssetId}
                  selectedNetworkId={selectedNetworkId}
                  onAssetChange={(assetId) => {
                    setSelectedAssetId(assetId);
                    setSelectedNetworkId("");
                    setCreateError("");
                  }}
                  onNetworkChange={(networkId) => {
                    setSelectedNetworkId(networkId);
                    setCreateError("");
                  }}
                  onCreate={createWallet}
                  creating={creating}
                  loadingNetworks={networksQuery.isLoading}
                  existingNetworkIds={existingNetworkIds}
                  error={createError}
                />
              </div>
            )}
          </div>
        </>
      )}

      <div className="rounded-xl border bg-card p-5 shadow-sm">
        <div>
          <h2 className="text-lg font-semibold">Transaction History</h2>
          <p className="text-sm text-muted-foreground">
            Your latest crypto wallet activity.
          </p>
        </div>

        <div className="mt-4 overflow-x-auto">
          {transactionsQuery.isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Loading transactions...
            </div>
          ) : transactions.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No crypto transactions yet.
            </div>
          ) : (
            <table className="w-full min-w-[700px] text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-3 py-3 font-medium">Type</th>
                  <th className="px-3 py-3 font-medium">Amount</th>
                  <th className="px-3 py-3 font-medium">Status</th>
                  <th className="px-3 py-3 font-medium">Reference</th>
                  <th className="px-3 py-3 font-medium">Date</th>
                </tr>
              </thead>

              <tbody>
                {transactions.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className="border-b last:border-0"
                  >
                    <td className="px-3 py-3">
                      {transaction.transaction_type || "—"}
                    </td>

                    <td className="px-3 py-3">
                      {transaction.amount ?? "—"}
                    </td>

                    <td className="px-3 py-3">
                      {transaction.status || "—"}
                    </td>

                    <td className="px-3 py-3">
                      {transaction.reference || "—"}
                    </td>

                    <td className="px-3 py-3">
                      {transaction.created_at
                        ? new Date(
                            transaction.created_at,
                          ).toLocaleString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal
        isOpen={Boolean(withdrawWallet)}
        onClose={closeWithdrawal}
        title={
          withdrawWallet
            ? `Withdraw ${assetMap.get(withdrawWallet.asset_id)?.symbol || "Crypto"}`
            : "Withdraw"
        }
        size="md"
      >
        {withdrawWallet && (
          <div className="space-y-5">
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-center gap-2 font-medium">
                <Network className="h-4 w-4" />
                Withdrawal Network
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted-foreground">Asset</p>
                  <p className="mt-1 font-medium">
                    {assetMap.get(withdrawWallet.asset_id)?.symbol || "USDT"}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground">Network</p>
                  <p className="mt-1 font-medium">
                    {withdrawWallet.network_name} (
                    {withdrawWallet.network_code})
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
              Send the withdrawal only to an address on{" "}
              <strong>
                {withdrawWallet.network_name} ({withdrawWallet.network_code})
              </strong>
              . Sending through another network may result in permanent loss.
            </div>

            {transactionPinQuery.isLoading ? (
              <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
                Checking Transaction PIN security...
              </div>
            ) : transactionPinQuery.isError ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                Unable to verify Transaction PIN status. Please refresh and try
                again.
              </div>
            ) : !transactionPinQuery.data?.transaction_pin_configured ? (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
                <p className="font-medium">Transaction PIN required</p>
                <p className="mt-1 text-muted-foreground">
                  You must configure a Transaction PIN in Settings → Security
                  before you can withdraw.
                </p>
              </div>
            ) : transactionPinQuery.data.pin_locked ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm">
                <p className="font-medium">
                  Transaction PIN temporarily locked
                </p>
                <p className="mt-1 text-muted-foreground">
                  Too many failed PIN attempts were detected. Wait for the
                  lockout period to expire before trying again.
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Amount (USDT)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={withdrawAmount}
                    onChange={(event) => {
                      const value = event.target.value.replace(
                        /[^0-9.]/g,
                        "",
                      );
                      setWithdrawAmount(value);
                      setWithdrawError("");
                    }}
                    placeholder="Minimum 5.00"
                    className="w-full rounded-lg border bg-background px-3 py-2.5"
                    disabled={withdrawing}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Minimum withdrawal: 5.00 USDT
                  </p>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Destination Address
                  </label>
                  <input
                    type="text"
                    autoComplete="off"
                    value={withdrawAddress}
                    onChange={(event) => {
                      setWithdrawAddress(event.target.value);
                      setWithdrawError("");
                    }}
                    placeholder="Enter destination wallet address"
                    className="w-full rounded-lg border bg-background px-3 py-2.5"
                    disabled={withdrawing}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Destination Tag{" "}
                    <span className="font-normal text-muted-foreground">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    autoComplete="off"
                    value={withdrawTag}
                    onChange={(event) => {
                      setWithdrawTag(event.target.value);
                      setWithdrawError("");
                    }}
                    placeholder="Only required by supported networks"
                    className="w-full rounded-lg border bg-background px-3 py-2.5"
                    disabled={withdrawing}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Transaction PIN
                  </label>
                  <input
                    type="password"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={6}
                    value={withdrawPin}
                    onChange={(event) => {
                      const value = event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 6);
                      setWithdrawPin(value);
                      setWithdrawError("");
                    }}
                    placeholder="Enter your 4 or 6 digit PIN"
                    className="w-full rounded-lg border bg-background px-3 py-2.5 tracking-widest"
                    disabled={withdrawing}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Your PIN is verified securely by MarisX and is never stored
                    in the browser.
                  </p>
                </div>

                <div className="rounded-lg border bg-muted/30 p-4 text-sm">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">
                      Withdrawal fee
                    </span>
                    <span className="font-medium">1.50 USDT</span>
                  </div>

                  {Number(withdrawAmount) > 0 && (
                    <div className="mt-2 flex items-center justify-between gap-4 border-t pt-2">
                      <span className="text-muted-foreground">
                        Total deducted
                      </span>
                      <span className="font-semibold">
                        {(Number(withdrawAmount) + 1.5).toFixed(2)} USDT
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}

            {withdrawError && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {withdrawError}
              </div>
            )}

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeWithdrawal}
                disabled={withdrawing}
                className="rounded-lg border px-4 py-2.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>

              {transactionPinQuery.data?.transaction_pin_configured &&
                !transactionPinQuery.data.pin_locked && (
                  <button
                    type="button"
                    onClick={submitWithdrawal}
                    disabled={
                      withdrawing ||
                      transactionPinQuery.isLoading ||
                      transactionPinQuery.isError
                    }
                    className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {withdrawing
                      ? "Submitting..."
                      : "Confirm Withdrawal"}
                  </button>
                )}
            </div>
          </div>
        )}
      </Modal>

      <div className="flex items-center gap-3 rounded-xl border bg-muted/30 p-4 text-sm text-muted-foreground">
        <LockKeyhole className="h-5 w-5 shrink-0" />

        <p>
          Your wallet keys are secured by MarisX. Private keys are never
          displayed in the user interface. Crypto withdrawals require your
          Transaction PIN and are subject to server-side security checks.
        </p>
      </div>
    </div>
  );
}

type WalletCreationFormProps = {
  assets: CryptoAsset[];
  networks: CryptoNetwork[];
  selectedAssetId: string;
  selectedNetworkId: string;
  onAssetChange: (assetId: string) => void;
  onNetworkChange: (networkId: string) => void;
  onCreate: () => void;
  creating: boolean;
  loadingNetworks: boolean;
  existingNetworkIds: Set<string>;
  error: string;
};

function WalletCreationForm({
  assets,
  networks,
  selectedAssetId,
  selectedNetworkId,
  onAssetChange,
  onNetworkChange,
  onCreate,
  creating,
  loadingNetworks,
  existingNetworkIds,
  error,
}: WalletCreationFormProps) {
  const supportedNetworks = networks.filter(
    (network) => network.chain_type === "EVM",
  );

  const comingSoonNetworks = networks.filter(
    (network) => network.chain_type !== "EVM",
  );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <label className="mb-2 block text-sm font-medium">
            Asset
          </label>

          <select
            value={selectedAssetId}
            onChange={(event) => onAssetChange(event.target.value)}
            className="w-full rounded-lg border bg-background px-3 py-2.5"
          >
            <option value="">Select asset</option>

            {assets.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.symbol} — {asset.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium">
            Network
          </label>

          <select
            value={selectedNetworkId}
            onChange={(event) => onNetworkChange(event.target.value)}
            disabled={!selectedAssetId || loadingNetworks}
            className="w-full rounded-lg border bg-background px-3 py-2.5 disabled:opacity-50"
          >
            <option value="">
              {loadingNetworks
                ? "Loading networks..."
                : "Select network"}
            </option>

            {supportedNetworks.map((network) => {
              const alreadyCreated = existingNetworkIds.has(network.id);

              return (
                <option
                  key={network.id}
                  value={network.id}
                  disabled={alreadyCreated}
                >
                  {network.network_name} ({network.network_code})
                  {alreadyCreated ? " — Already created" : ""}
                </option>
              );
            })}

            {comingSoonNetworks.map((network) => (
              <option key={network.id} value="" disabled>
                {network.network_name} ({network.network_code}) — Coming soon
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-end">
          <button
            type="button"
            onClick={onCreate}
            disabled={
              creating ||
              !selectedAssetId ||
              !selectedNetworkId ||
              existingNetworkIds.has(selectedNetworkId)
            }
            className="w-full rounded-lg bg-primary px-4 py-2.5 font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {creating ? "Creating wallet..." : "Create Wallet"}
          </button>
        </div>
      </div>

      {selectedNetworkId && (
        <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
          {(() => {
            const network = networks.find(
              (item) => item.id === selectedNetworkId,
            );

            if (!network) return null;

            return (
              <>
                <strong className="text-foreground">
                  {network.network_name} ({network.network_code})
                </strong>{" "}
                wallet creation is currently available through the{" "}
                <strong className="text-foreground">
                  {network.chain_type}
                </strong>{" "}
                wallet engine.
              </>
            );
          })()}
        </div>
      )}

      {comingSoonNetworks.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
          Other configured networks may appear here as{" "}
          <strong>Coming soon</strong> until their wallet infrastructure is
          fully supported by MarisX.
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}
    </div>
  );
}
